import { debounce } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { useMap, useMapsLibrary, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { API_FIELDS_CONTACT } from '../configs';

export const useGoogleAddressAutocomplete = (
  handlePlaceDetails: (details: google.maps.places.PlaceResult | null, selectedPrediction: google.maps.places.AutocompletePrediction | null) => void,
  restrictToUS?: boolean,
) => {
  const map = useMap();
  const places = useMapsLibrary('places');
  const apiIsLoaded = useApiIsLoaded();
  const [selectedSuggestion, setSelectedSuggestion] = useState<google.maps.places.AutocompletePrediction | null>(null);
  const [loading, setLoading] = useState(false);

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteSessionToken
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);

  // https://developers.google.com/maps/documentation/javascript/reference/places-service
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);

  const [predictionResults, setPredictionResults] = useState<Array<google.maps.places.AutocompletePrediction>>([]);

  useEffect(() => {
    if (!places || !map || !apiIsLoaded) return;
    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [map, places, apiIsLoaded]);

  const fetchPredictions = useCallback(
    async (inputValue: string) => {
      const componentRestrictions = restrictToUS ? { [API_FIELDS_CONTACT.country]: 'us' } : undefined;
      setLoading(true);
      if (!autocompleteService || !inputValue) {
        setPredictionResults([]);
        return;
      }
      try {
        const response = await autocompleteService.getPlacePredictions({ input: inputValue, sessionToken, componentRestrictions });

        if (selectedSuggestion) {
          setPredictionResults([selectedSuggestion, ...response.predictions]);
        } else {
          setPredictionResults(response.predictions);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [autocompleteService, sessionToken, selectedSuggestion, restrictToUS],
  );

  const onInputChange = useCallback(
    (value: string) => {
      debounce(fetchPredictions, 400)(value);
    },
    [fetchPredictions],
  );

  const handleSuggestionClick = useCallback(
    (selectedPrediction: google.maps.places.AutocompletePrediction | null) => {
      if (!places || !selectedPrediction) return;

      const detailRequestOptions = {
        placeId: selectedPrediction.place_id,
        fields: ['geometry', 'name', 'formatted_address', 'address_components'],
        sessionToken,
      };

      const detailsRequestCallback = (placeDetails: google.maps.places.PlaceResult | null) => {
        handlePlaceDetails(placeDetails, selectedPrediction);
        setPredictionResults([]);
        // setInputValue(placeDetails?.formatted_address ?? '');
        setSessionToken(new places.AutocompleteSessionToken());
        setSelectedSuggestion(selectedPrediction);
      };

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
    },
    [handlePlaceDetails, places, placesService, sessionToken, setSelectedSuggestion],
  );

  return { handleSuggestionClick, onInputChange, predictionResults, selectedSuggestion, loading };
};
